import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const PublicEstimate = () => {
  const { estimateId } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEstimate();
  }, [estimateId]);

  const fetchEstimate = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await axios.get(`${backendUrl}/api/estimates/public/${estimateId}`);
      setEstimate(response.data);
    } catch (err) {
      console.error('Error fetching estimate:', err);
      setError(err.response?.data?.detail || 'Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your estimate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Estimate Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!estimate) return null;

  const contractor = estimate.contractor || {};
  const primaryColor = contractor.primary_color || '#F97316';
  const secondaryColor = contractor.secondary_color || '#1E40AF';
  const finalPrice = estimate.discounted_price || estimate.total_cost || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Logo/Company Name */}
        <div className="bg-white rounded-t-xl shadow-lg p-6 border-b-4" style={{ borderColor: primaryColor }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {contractor.logo_url ? (
                <img 
                  src={contractor.logo_url} 
                  alt={contractor.company_name} 
                  className="h-16 w-auto object-contain"
                />
              ) : (
                <div className="text-2xl font-bold" style={{ color: primaryColor }}>
                  {contractor.company_name}
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Estimate Generated</p>
              <p className="text-sm font-semibold text-gray-800">{formatDate(estimate.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Special Offer Banner */}
        <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 text-white p-6 shadow-lg animate-pulse">
          <div className="flex items-center justify-center gap-3">
            <div className="text-4xl">🔥</div>
            <div className="text-center">
              <p className="text-2xl font-bold mb-1">LIMITED TIME OFFER!</p>
              <p className="text-lg font-semibold">
                Call within 24 hours and receive 5% OFF your total estimate!
              </p>
              <p className="text-sm mt-1 opacity-90">Don't miss this exclusive savings opportunity</p>
            </div>
            <div className="text-4xl">🔥</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow-lg p-8">
          {/* Property Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: secondaryColor }}>
              Property Information
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Property Address</p>
                  <p className="text-lg font-semibold text-gray-800">{estimate.address}</p>
                </div>
                {estimate.customer_name && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Customer Name</p>
                    <p className="text-lg font-semibold text-gray-800">{estimate.customer_name}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Satellite Map */}
          {estimate.static_map_url && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: secondaryColor }}>
                Satellite View
              </h2>
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src={estimate.static_map_url} 
                  alt="Property satellite view" 
                  className="w-full h-auto"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              {estimate.imagery_date && (
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Imagery Date: {estimate.imagery_date}
                </p>
              )}
            </div>
          )}

          {/* Roof Measurements */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: secondaryColor }}>
              Roof Measurements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
                <p className="text-sm text-gray-600 mb-2">Total Area</p>
                <p className="text-3xl font-bold text-blue-700">
                  {estimate.square_footage?.toLocaleString() || 'N/A'} sq ft
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
                <p className="text-sm text-gray-600 mb-2">Roof Squares</p>
                <p className="text-3xl font-bold text-green-700">
                  {estimate.roof_squares?.toFixed(1) || 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  (with waste: {estimate.roof_squares_with_waste?.toFixed(1) || 'N/A'})
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-6 border-l-4 border-purple-500">
                <p className="text-sm text-gray-600 mb-2">Accuracy</p>
                <p className="text-3xl font-bold text-purple-700">
                  {estimate.accuracy_rating?.toFixed(1) || 'N/A'}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Satellite precision</p>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: secondaryColor }}>
              Cost Breakdown
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Materials</span>
                  <span className="text-lg font-semibold text-gray-800">
                    {formatCurrency(estimate.materials_cost)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Labor</span>
                  <span className="text-lg font-semibold text-gray-800">
                    {formatCurrency(estimate.labor_cost)}
                  </span>
                </div>
                {estimate.steep_roof_applied && estimate.steep_roof_charge > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">🏔️ Steep Roof Charge</span>
                    <span className="text-lg font-semibold text-gray-800">
                      {formatCurrency(estimate.steep_roof_charge)}
                    </span>
                  </div>
                )}
                {estimate.discount_percent > 0 && (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Subtotal</span>
                      <span className="text-lg font-semibold text-gray-800">
                        {formatCurrency(estimate.total_cost)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-green-600 font-semibold">
                        Discount ({estimate.discount_percent}%)
                      </span>
                      <span className="text-lg font-semibold text-green-600">
                        -{formatCurrency(estimate.total_cost - finalPrice)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center py-4 mt-2">
                  <span className="text-xl font-bold text-gray-800">Total Estimate</span>
                  <span className="text-3xl font-bold" style={{ color: primaryColor }}>
                    {formatCurrency(finalPrice)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* All Roof Type Pricing - 2x2 Grid */}
          {estimate.all_roof_pricing && Object.keys(estimate.all_roof_pricing).length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: secondaryColor }}>
                Pricing by Roof Type
              </h2>
              <p className="text-gray-600 mb-4 text-sm">Compare pricing across different roofing material options for your property:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(estimate.all_roof_pricing).map(([key, pricing]) => (
                  <div 
                    key={key} 
                    className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200 hover:border-orange-300 transition-colors text-center"
                  >
                    <p className="text-sm font-medium text-gray-500 mb-2">{pricing.display_name}</p>
                    <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                      {formatCurrency(pricing.total)}
                    </p>
                    <div className="mt-2 text-xs text-gray-400 space-x-3">
                      <span>Materials: {formatCurrency(pricing.materials)}</span>
                      <span>Labor: {formatCurrency(pricing.labor)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photos */}
          {estimate.photos && estimate.photos.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4" style={{ color: secondaryColor }}>
                Property Photos
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {estimate.photos.map((photo, index) => (
                  <div key={index} className="rounded-lg overflow-hidden shadow-md">
                    <img 
                      src={photo.url || photo.supabase_url} 
                      alt={`Property photo ${index + 1}`}
                      className="w-full h-48 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-lg mb-6">Contact us today to schedule your roofing project!</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {contractor.contact_phone && (
                <a 
                  href={`tel:${contractor.contact_phone}`}
                  className="bg-white text-blue-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition flex items-center justify-center gap-2"
                >
                  <span className="text-xl">📞</span>
                  {contractor.contact_phone}
                </a>
              )}
              {contractor.contact_email && (
                <a 
                  href={`mailto:${contractor.contact_email}`}
                  className="bg-white text-orange-600 font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition flex items-center justify-center gap-2"
                >
                  <span className="text-xl">✉️</span>
                  {contractor.contact_email}
                </a>
              )}
            </div>

            {contractor.contact_address && (
              <div className="mt-6">
                <p className="text-sm opacity-90">📍 {contractor.contact_address}</p>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-white border-opacity-30">
              <p className="text-xs opacity-75 leading-relaxed">
                <span className="font-semibold">Disclaimer:</span> This estimate is generated using satellite imagery and AI-powered measurement technology. While highly accurate, a physical on-site inspection is required to confirm all conditions, including hidden damage, structural factors, or site-specific variables not identifiable through remote estimation. Final pricing may vary based on inspection findings.
              </p>
              <p className="text-xs opacity-60 mt-2">
                Powered by {contractor.company_name || 'HomeFixer360'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-800 text-white text-center py-6 rounded-b-xl shadow-lg">
          <p className="text-sm">
            Questions about your estimate? Contact us at{' '}
            {contractor.contact_phone && (
              <a href={`tel:${contractor.contact_phone}`} className="text-orange-400 hover:underline">
                {contractor.contact_phone}
              </a>
            )}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            © {new Date().getFullYear()} {contractor.company_name || 'HomeFixer360'}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicEstimate;
